/*
 * waitlist (page)
 */
import { appConfig } from "@emr/config"
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
	Divider,
	Page,
} from "@emr/react-ui"
import { usePushNotification } from "@emr/push-notifications"
import { createFileRoute } from "@tanstack/react-router"
import { Bell, BellRing, CheckCircle } from "lucide-react"
import { useTranslation } from "react-i18next"
import { authPageLoaderAuthenticated } from "../utils/auth.utils"

export const Route = createFileRoute("/waitlist")({
	component: WaitlistPage,
	loader: authPageLoaderAuthenticated(appConfig.redirects.afterSignInUrl),
})

function WaitlistPage() {
	const { user } = Route.useLoaderData()

	return (
		<Page className={"bg-primary/30"}>
			<Card className={"w-full max-w-md p-8 pb-12 text-center"}>
				<CardHeader className={"items-center gap-3"}>
					<div
						className={
							"w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center"
						}
					>
						<CheckCircle className={"w-7 h-7 text-primary"} />
					</div>
					<CardTitle className={"text-2xl"}>Registration Received</CardTitle>
					<CardDescription className={"text-base"}>
						Thank you for registering.
					</CardDescription>
				</CardHeader>
				<CardContent className={"flex flex-col gap-4"}>
					<p className={"text-muted-foreground text-sm"}>
						The clinic will review your file and will be in contact shortly.
					</p>
					<Divider />
					<WaitlistPushOption userId={user.id} />
				</CardContent>
			</Card>
		</Page>
	)
}

function WaitlistPushOption({ userId }: { userId: string }) {
	const { t } = useTranslation()
	const { isSupported, subscription, subscribe, unsubscribe } =
		usePushNotification({ userId })

	if (!isSupported) {
		return null
	}

	return subscription ? (
		<div className={"flex flex-col items-center gap-2"}>
			<div className={"flex items-center gap-2 text-sm text-primary"}>
				<BellRing className={"w-4 h-4"} />
				<span>{t("waitlist.push.notified")}</span>
			</div>
			<button
				className={
					"text-xs text-muted-foreground underline underline-offset-2 cursor-pointer"
				}
				onClick={unsubscribe}
			>
				{t("waitlist.push.turnOff")}
			</button>
		</div>
	) : (
		<div className={"flex flex-col items-center gap-2"}>
			<p className={"text-sm text-muted-foreground"}>
				{t("waitlist.push.prompt")}
			</p>
			<Button
				variant={"outline"}
				size={"sm"}
				className={"gap-2"}
				onClick={subscribe}
			>
				<Bell className={"w-4 h-4"} />
				{t("waitlist.push.enable")}
			</Button>
		</div>
	)
}
